import type {
  EnvelopeBalance,
  EnvelopeId,
  LedgerHistory,
  PeriodId,
  PeriodOpening,
  Xof,
} from '@/domain'

import type { Json } from './database.types'
import type { CentimeClient } from './supabase'

/**
 * État d'une période calculé par la base : soldes de référence, ouverture et historique utile.
 * C'est la source de vérité ; le domaine local s'en sert de point de départ.
 */
export interface PeriodSnapshot {
  periodId: PeriodId
  status: 'open' | 'closed'
  timeZone: string
  startDay: number
  opening: PeriodOpening
  incomeReceived: Xof
  unallocated: Xof
  allocatedTotal: Xof
  spentTotal: Xof
  envelopes: Record<EnvelopeId, EnvelopeBalance>
  history: LedgerHistory
}

class SnapshotError extends Error {
  override name = 'SnapshotError'
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new SnapshotError(`${path} : objet attendu`)
  }
  return value as Record<string, unknown>
}

function integer(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) {
    throw new SnapshotError(`${path} : entier attendu`)
  }
  return value
}

function text(value: unknown, path: string): string {
  if (typeof value !== 'string') throw new SnapshotError(`${path} : texte attendu`)
  return value
}

const BALANCE_FIELDS = [
  'openingBalance',
  'allocated',
  'transferredIn',
  'transferredOut',
  'spent',
  'adjusted',
  'balance',
  'reserved',
  'available',
] as const

export function parsePeriodSnapshot(value: Json): PeriodSnapshot {
  const root = record(value, 'état')
  const opening = record(root.opening, 'opening')
  const history = record(root.history, 'history')
  const status = text(root.status, 'status')
  if (status !== 'open' && status !== 'closed')
    throw new SnapshotError(`status inconnu : ${status}`)

  const openingEnvelopes: PeriodOpening['envelopes'] = Object.fromEntries(
    Object.entries(record(opening.envelopes, 'opening.envelopes')).map(([id, raw]) => {
      const item = record(raw, `opening.envelopes.${id}`)
      return [
        id,
        { balance: integer(item.balance, 'balance'), reserved: integer(item.reserved, 'reserved') },
      ]
    }),
  )

  const envelopes = Object.fromEntries(
    Object.entries(record(root.envelopes, 'envelopes')).map(([id, raw]) => {
      const item = record(raw, `envelopes.${id}`)
      const balance = Object.fromEntries(
        BALANCE_FIELDS.map((field) => [field, integer(item[field], `envelopes.${id}.${field}`)]),
      ) as unknown as EnvelopeBalance
      return [id, balance]
    }),
  )

  const settled = history.settledOccurrences
  if (!Array.isArray(settled))
    throw new SnapshotError('history.settledOccurrences : liste attendue')

  return {
    periodId: text(root.periodId, 'periodId'),
    status,
    timeZone: text(root.timeZone, 'timeZone'),
    startDay: integer(root.startDay, 'startDay'),
    opening: {
      unallocated: integer(opening.unallocated, 'opening.unallocated'),
      envelopes: openingEnvelopes,
    },
    incomeReceived: integer(root.incomeReceived, 'incomeReceived'),
    unallocated: integer(root.unallocated, 'unallocated'),
    allocatedTotal: integer(root.allocatedTotal, 'allocatedTotal'),
    spentTotal: integer(root.spentTotal, 'spentTotal'),
    envelopes,
    history: {
      settledOccurrences: new Set(settled.map((id) => text(id, 'history.settledOccurrences'))),
      openReservations: Object.fromEntries(
        Object.entries(record(history.openReservations, 'history.openReservations')).map(
          ([id, raw]) => {
            const item = record(raw, `history.openReservations.${id}`)
            return [
              id,
              {
                envelopeId: text(item.envelopeId, 'envelopeId'),
                amount: integer(item.amount, 'amount'),
              },
            ]
          },
        ),
      ),
    },
  }
}

export async function fetchPeriodState(
  client: CentimeClient,
  periodId?: PeriodId,
): Promise<PeriodSnapshot> {
  const { data, error } = await client.rpc(
    'get_period_state',
    periodId === undefined ? {} : { p_period_id: periodId },
  )
  if (error) throw error
  return parsePeriodSnapshot(data)
}
